import { PathnameDetailType } from "@/app/types";
import api from "@v2/lib/api";
import { notFound } from "next/navigation";
import { cache } from "react";
import { LandingPageData, LPResponse } from "./types";

export const getLandingPage = cache(async (slug: string): Promise<LPResponse> => {
	const response = await fetch(api.landing_page(slug), {
		next: {
			tags: ["landingPage"],
			revalidate: 3600,
		},
	});
	if (!response.ok) return notFound();
	return await response.json();
});

// First non-empty helper
function pickFirst<T extends string | undefined | null>(...vals: T[]): string | undefined {
	return vals.find((v) => typeof v === "string" && v.trim() !== "") as string | undefined;
}

// Media URL builder
function mediaUrl(id?: string | number, width = 400, quality = 80) {
	if (!id) return undefined;
	return `${process.env.NEXT_PUBLIC_MEDIA_URL}/api/v1/media/${id}?width=${width}&quality=${quality}`;
}

/**
 * The trail a landing page sits in. It used to be for search results only, because no
 * landing page drew one. A hero component does, and when the page carries one the trail
 * an editor wrote there is the trail the reader sees - so that is the one published,
 * rather than a second version of it assembled from the address.
 *
 * Without a hero the levels still come from the public address that points at the
 * landing, so a page with nothing above it is published as the home page and itself.
 */
export function BuildLandingBreadcrumbJsonLd(pathnameDetails: PathnameDetailType | null, page: LandingPageData) {
	const drawn = readHeroBreadcrumb(page);
	if (drawn) return buildBreadcrumbJsonLd(drawn);

	const label = pickFirst(pathnameDetails?.reverse_eq_path?.breadcrumb_title, page.title);
	if (!label) return null;

	const trail = [
		{ label: "خانه", href: "/fa" },
		...(pathnameDetails?.breadcrumb ?? []).map((level) => ({ label: level.title, href: level.source })),
		// The page being read closes the trail and carries no url of its own.
		{ label, href: undefined as string | undefined },
	];

	return buildBreadcrumbJsonLd(trail);
}

// The trail the first hero on the page draws. A landing page opens on one header, so
// the first is the one the reader sees; a hero drawn on a phone only is still the trail
// the page states it sits in, and hiding it per viewport says nothing about that.
function readHeroBreadcrumb(page: LandingPageData): { label: string; href?: string }[] | null {
	const hero = page.components?.find((component) => component.name === "hero");
	const crumbs: { title?: string; link?: string | null }[] = hero?.content?.data?.breadcrumbs ?? [];

	const trail = crumbs
		.filter((crumb) => crumb?.title)
		.map((crumb) => ({ label: crumb.title!, href: crumb.link || undefined }));

	return trail.length ? trail : null;
}

function buildBreadcrumbJsonLd(trail: { label: string; href?: string }[]) {
	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"itemListElement": trail.map((level, index) => ({
			"@type": "ListItem",
			"position": index + 1,
			"name": level.label,
			// An address already absolute - another site, or a full url - is published
			// as written; a site path is completed with the home it belongs to.
			...(level.href ? { "item": level.href.startsWith("http") ? level.href : `${process.env.NEXT_PUBLIC_HOME_URL}${level.href}` } : {}),
		})),
	};
}

export function BuildLandingPageMetadata(response: LPResponse, pathnameDetails: PathnameDetailType) {
	const eq = pathnameDetails?.eq_path?.seo;
	const rev = pathnameDetails?.reverse_eq_path?.seo;

	// ----- PAGE_TITLE
	const PAGE_TITLE = pickFirst(rev?.title, eq?.title, response.data.seo?.title, response.data.title, "عینک وحدت | مرجع تخصصی عینک")!;

	// ----- PAGE_DESCRIPTION
	const PAGE_DESCRIPTION = pickFirst(rev?.description, eq?.description, response.data.seo?.description, "عینک وحدت")!;

	const CANONICAL_TAG =
		process.env.NEXT_PUBLIC_HOME_URL +
		pickFirst(
            rev?.canonical,
			eq?.canonical,
			response.data.seo?.canonical,
			pathnameDetails?.reverse_eq_path?.source,
			pathnameDetails?.eq_path?.source,
			"/fa/landing/" + response.data.slug
		)!;

	// ----- IMAGE
	const IMAGE = null;

	// ----- OG_IMAGE (eq=id -> mediaUrl, others assume URL/absolute per your code)
	const OG_IMAGE =
		pickFirst(
			pathnameDetails?.reverse_eq_path?.seo?.og?.image ? mediaUrl(pathnameDetails.reverse_eq_path.seo.og.image, 480, 80) : undefined,
			eq?.og?.image ? mediaUrl(eq.og.image, 480, 80) : undefined,
			IMAGE || undefined
		) || undefined;

	// ----- OG_TITLE / OG_DESCRIPTION
	const OG_TITLE = pickFirst(rev?.og?.title, eq?.og?.title, PAGE_TITLE)!;

	const OG_DESCRIPTION = pickFirst(rev?.og?.description, eq?.og?.description, PAGE_DESCRIPTION)!;

	return {
		PAGE_TITLE,
		PAGE_DESCRIPTION,
		CANONICAL_TAG,
		IMAGE,
		OG_IMAGE,
		OG_TITLE,
		OG_DESCRIPTION,
	};
}
